import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Chip,
  Tab,
  Tabs,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircle as PlayCircleIcon,
  Schedule as ScheduleIcon,
  RateReview as ReviewIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import dataStrategyModulesService, { 
  DataStrategyModule, 
  ProgressSummary 
} from '../services/dataStrategyModulesService';

const DataStrategyModules: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [modules, setModules] = useState<DataStrategyModule[]>([]);
  const [progressSummary, setProgressSummary] = useState<ProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [modulesData, summaryData] = await Promise.all([
        dataStrategyModulesService.getAllModules(),
        dataStrategyModulesService.getProgressSummary()
      ]);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      setProgressSummary(summaryData);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load modules. Please try again.');
      setModules([]); // Set empty array on error
      setProgressSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'primary';
      case 'Under Review':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircleIcon />;
      case 'In Progress':
        return <PlayCircleIcon />;
      case 'Under Review':
        return <ReviewIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const filterModules = (modules: DataStrategyModule[]) => {
    if (!modules || !Array.isArray(modules)) {
      return [];
    }
    switch (tabValue) {
      case 1: // In Progress
        return modules.filter(m => m.status === 'In Progress');
      case 2: // Completed
        return modules.filter(m => m.status === 'Completed');
      case 3: // Not Started
        return modules.filter(m => m.status === 'Not Started');
      default: // All Modules
        return modules;
    }
  };

  const filteredModules = filterModules(modules);
  const safeModules = modules || [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
          UC Berkeley Data Strategy Program
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track your progress through the 12-module Data Strategy certification program
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Progress Summary Dashboard */}
      {progressSummary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ borderTop: '4px solid #003366' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Overall Progress
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {progressSummary.overallProgress}%
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progressSummary.overallProgress} 
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderTop: '4px solid #4caf50' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Completed
                </Typography>
                <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                  {progressSummary.completed}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of {progressSummary.totalModules} modules
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderTop: '4px solid #2196f3' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  In Progress
                </Typography>
                <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                  {progressSummary.inProgress}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  active modules
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ borderTop: '4px solid #ff9800' }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Under Review
                </Typography>
                <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                  {progressSummary.underReview}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  awaiting feedback
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs for filtering */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`All Modules (${safeModules.length})`} />
          <Tab label={`In Progress (${safeModules.filter(m => m.status === 'In Progress').length})`} />
          <Tab label={`Completed (${safeModules.filter(m => m.status === 'Completed').length})`} />
          <Tab label={`Not Started (${safeModules.filter(m => m.status === 'Not Started').length})`} />
        </Tabs>
      </Box>

      {/* Module Cards */}
      <Grid container spacing={3}>
        {filteredModules.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">
              No modules found in this category. Check back later or select a different filter.
            </Alert>
          </Grid>
        ) : (
          filteredModules.map((module) => (
            <Grid item xs={12} md={6} lg={4} key={module.moduleNumber}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `4px solid ${
                    module.status === 'Completed' ? '#4caf50' :
                    module.status === 'In Progress' ? '#2196f3' :
                    module.status === 'Under Review' ? '#ff9800' :
                    '#9e9e9e'
                  }`,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Chip 
                      icon={getStatusIcon(module.status)}
                      label={module.status}
                      color={getStatusColor(module.status) as any}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Module {module.moduleNumber}
                    </Typography>
                  </Box>

                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
                    {module.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {module.description.substring(0, 150)}...
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {module.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={module.progress} 
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip 
                      label={`${module.learningObjectives.filter(o => o.completed).length}/${module.learningObjectives.length} Objectives`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip 
                      label={`${module.sections.length} Sections`}
                      size="small"
                      variant="outlined"
                    />
                    {module.dueDate && (
                      <Chip 
                        label={`Due: ${new Date(module.dueDate).toLocaleDateString()}`}
                        size="small"
                        variant="outlined"
                        color={new Date(module.dueDate) < new Date() ? 'error' : 'default'}
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate(`/admin/data-strategy-modules/${module.moduleNumber}`)}
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }}
                  >
                    {module.status === 'Not Started' ? 'Start Module' : 'Continue Module'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
};

export default DataStrategyModules;
